import { ApolloCache, useMutation } from '@apollo/client';

import {
  AdTypeEnum,
  CreateBookmarkMutation,
  CreateBookmarkMutationVariables,
  DeleteBookmarkMutation,
  DeleteBookmarkMutationVariables,
  Resume,
  Vacancy
} from '@/__generated_graphql__/graphql';
import { STATUS_CODES } from '@/constants/common';
import {
  CREATE_BOOKMARK,
  DELETE_BOOKMARK
} from '@/graphql/mutations/bookmark.mutation';

const RESUME_TYPENAME: Resume['__typename'] = 'Resume';
const VACANCY_TYPENAME: Vacancy['__typename'] = 'Vacancy';
type AdTypename = Resume['__typename'] | Vacancy['__typename'];

export const useToggleBookmark = () => {
  const updateBookmarkCache = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: ApolloCache<any>,
    variables: { id: string; adType: AdTypeEnum },
    isBookmarked: boolean
  ) => {
    const typename: AdTypename =
      variables.adType === AdTypeEnum.Resume
        ? RESUME_TYPENAME
        : VACANCY_TYPENAME;

    const idFromStore = cache.identify({
      __typename: typename,
      id: variables.id
    });

    return cache.modify({
      id: idFromStore,
      fields: {
        isBookmarked: () => isBookmarked
      }
    });
  };

  const [createBookmark] = useMutation<
    CreateBookmarkMutation,
    CreateBookmarkMutationVariables
  >(CREATE_BOOKMARK, {
    update(cache, { data }, { variables }) {
      if (!variables) return;
      const isSuccess = data?.createBookmark?.status === STATUS_CODES.SUCCESS;
      return updateBookmarkCache(cache, variables, isSuccess);
    }
  });

  const [deleteBookmark] = useMutation<
    DeleteBookmarkMutation,
    DeleteBookmarkMutationVariables
  >(DELETE_BOOKMARK, {
    update(cache, { data }, { variables }) {
      if (!variables) return;
      const isSuccess = data?.deleteBookmark?.status === STATUS_CODES.SUCCESS;
      return updateBookmarkCache(cache, variables, !isSuccess);
    }
  });

  const toggleBookmark = (
    id: string,
    type: AdTypeEnum,
    isBookmarked: boolean
  ) => {
    const queryArgs = {
      variables: { id, adType: type }
    };

    const queryCallback = isBookmarked ? deleteBookmark : createBookmark;
    return queryCallback(queryArgs).catch(error =>
      console.error(
        `Error during ${isBookmarked ? 'delete' : 'add'} bookmark`,
        error
      )
    );
  };

  return toggleBookmark;
};
